/**
 * shared/protocol.ts — the co-read wire, imported by the Fastify server and by the reader.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * SYNC THE CONTROLS, NOT THE MEDIA.
 *
 * movies.karti.ai's one key idea, and the reason that product is cheap and works. The server never
 * streams into a room. It holds ONE authoritative clock, relays *intents*, and every client drives
 * its own local player toward that clock — so two people stay together while volume, mute, theme,
 * font size and screen width stay entirely personal. Nothing here carries audio.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **The position is a `BookPos`, not a float on a timeline.** A phone and a MacBook share a room
 * because they share a *sentence*. They do not share a page number — they cannot, because a display
 * page is a client-computed column that changes with the font size (§2.3) — and they do not share a
 * millisecond, because `ms` moves every time a page is narrated (LAW 3). `positionSec` rides
 * alongside so movies' clock and drift loop port verbatim, and it is what the scrubber renders; the
 * `sid` is what everything *resolves* from.
 *
 * ⚠️ **LAW 19. `advance` and `loadBook` are different operations.** movies' `setSource` clears the
 * annotations, resets the rate to 1, zeroes the buffering flags and pauses. That is correct when you
 * change films and catastrophic here, where turning onto the next page is the single most common
 * thing that happens in a room. All three P5 proposals ported it unchanged.
 *
 * ⚠️ **LAW 20. Every block carries a deadline.** See `BLOCK_DEADLINE_MS`.
 *
 * The identity on this wire is the **actorKey** (`usr_…` / `gst_…`) that the `abk` cookie already
 * proved, never a name the browser claimed. movies' own comment records what the other way costs:
 * *"a member's name used to be whatever the browser claimed in its `hello`, so anyone could sit in a
 * theater as anyone."*
 */

import type { BookPos, PlaybackMode } from "./position.ts";

export type { BookPos, PlaybackMode } from "./position.ts";

// ─── the room's shape ────────────────────────────────────────────────────────

/** Who may drive playback. `everyone` includes guests — see `canControl`. */
export type ControlMode = "everyone" | "host";

/**
 * How a room appears in the lobby.
 *  - `shown`  — name and book visible; a password, if set, is still required to enter.
 *  - `masked` — listed as an anonymous "Private reading"; name, book and position hidden.
 *  - `hidden` — not listed at all, reachable only by its code. **The default here**, unlike movies:
 *               a reading room is *the Bible with Dad on Tuesdays*, not a public theater.
 */
export type RoomListing = "shown" | "masked" | "hidden";

/** What a member is waiting for. The room's `blockedBy` is a list of these, with deadlines. */
export type NotReady = "buffering" | "generating" | "gesture" | "behind";

/**
 * ⚠️ **LAW 20. Every co-read block has a deadline, and a block that only a successful job can clear
 * is a deadlock.**
 *
 * `generating` originally had none. Combined with `ensure` answering 503-and-no-job while spark-1 is
 * down, a reboot mid-session freezes the Tuesday room **forever** behind a banner reading *"about 25
 * seconds"* — for everyone, including the members whose next page is already cached. On deadline, or
 * immediately when the enqueue cannot be made at all, the block is dropped, the room resumes, and
 * that member is told the honest state (*"the voice service is offline — you're reading ahead of the
 * narration"*) rather than being left in front of a lie with a number in it.
 *
 * `behind` is `null` deliberately and it is not an exception to the law. It is the only reason that
 * is **derived live on every recompute** rather than latched by a report: it is recomputed from the
 * member's own `ord` against the room's, so it clears the instant they catch up, turn a page, or
 * disconnect. A latched flag needs a deadline because it can outlive the condition; a derived one
 * cannot.
 */
export const BLOCK_DEADLINE_MS: Readonly<Record<NotReady, number | null>> = {
  buffering: 60_000,
  generating: 120_000,
  gesture: 20_000,
  behind: null,
};

/**
 * `gesture` blocks only in the first twenty seconds after joining.
 *
 * Autoplay refusal is a real condition and it is worth pausing the room for once, at the start, so
 * the person who just opened the link is not left behind. Past that window a member who has not
 * tapped is a tab somebody abandoned, and honouring their block would let one forgotten iPad hold
 * three people at a paragraph indefinitely.
 */
export const GESTURE_WINDOW_MS = 20_000;

export interface Member {
  /** The actorKey. `usr_…` or `gst_…` — from the cookie, never from `hello`. */
  key: string;
  name: string;
  color: string;
  /**
   * From `displayKindOf()` in shared/actor.ts, so the presence rail matches the access model. A
   * guest is ALWAYS drawn with a distinct chip and a dotted ring: a guest can never look like an
   * account.
   */
  kind: "guest" | "user" | "god";
  isHost: boolean;
  /** May this member control playback right now (see `canControl`). */
  holdsRemote: boolean;
  connected: boolean;
  /** False once they browsed away from the narration. Turning a page is browsing — LAW 12. */
  following: boolean;
  /** Where they are. `null` until their first `{t:"at"}`. */
  at: BookPos | null;
  /** What they are waiting for, or null. Only a CONNECTED member's block counts — see `Playback`. */
  blocked: NotReady | null;
  joinedAt: number;
}

/** The client half of P5 spells it `RoomMember`. One type, two names, zero conversion functions. */
export type RoomMember = Member;

/**
 * The authoritative playback state. §7.3, and `positionSec`/`anchorServerTime`/`rate` are movies'
 * shape field for field so that `effectivePosition()` and `reanchor()` port character for character.
 *
 * The effective position at any moment is DERIVED, never stored — `predictPosition()` in
 * shared/position.ts is the one formula, and `mode: "read"` has no clock at all.
 */
export interface Playback {
  bookId: string;
  editionId: string;
  mode: PlaybackMode;
  playing: boolean;
  positionSec: number;
  /** Server epoch ms at which `positionSec` was captured. */
  anchorServerTime: number;
  rate: number;
  /** The truth. `positionSec` is the rendering of it on the pinned axis. */
  at: BookPos;
  /**
   * ⚠️ **The room pins `book_timeline.version` at open** (§7.3). All arithmetic inside the room uses
   * the pinned axis for the session: everyone shares the same slightly-stale numbers, which is
   * harmless *because it is shared*. A narration landing mid-session would otherwise silently move
   * everyone. Migration happens only at a page boundary or an explicit resync, and migrates by
   * `sid` → `ms`, NEVER `ms` → `sid`.
   */
  timelineVersion: string | null;
  voiceId: string;
  /** True while the room is auto-paused because somebody connected is blocked. */
  waitingForEveryone: boolean;
  /** Null, never `[]`, when nobody is blocking — so "is anything blocking" is one falsy test. */
  blockedBy: BlockedBy[] | null;
}

export interface BlockedBy {
  memberKey: string;
  reason: NotReady;
  /** What the member was told it would take. Advisory; the deadline is what actually clears it. */
  etaSec?: number;
  /** The page the server enqueued on their behalf, for `generating`. */
  pageId?: string;
  jobId?: string;
  /** Epoch ms at which this block expires and the room moves on regardless. Null only for `behind`. */
  deadlineAt: number | null;
}

export interface RoomSnapshot {
  code: string;
  name: string;
  bookId: string;
  editionId: string;
  /** Every host. Any host has full admin powers over the room. Never empty. */
  hostKeys: string[];
  ownerKey: string;
  controlMode: ControlMode;
  mode: PlaybackMode;
  waitForEveryone: boolean;
  /**
   * **Sentences** of slack before a member counts as `behind` in read mode.
   *
   * ⚠️ It said *pages* here, and `shared/position.ts:isBehind` — the one implementation — says the
   * opposite in as many words: *"`slack` is in `ord` units — sentences, because `ord` is the only unit
   * the rule is stated in and the only one that means the same thing on a phone and a MacBook. It is
   * deliberately not pages: a canonical page is ~1500 characters and a display page is whatever the
   * reader's font size makes it, so 'one page of slack' is two different amounts of slack for the two
   * people in the room."* `Room.applySettings` clamps it to `[0, 10 000]`, which is only sensible in
   * sentences. §2.4's exact failure — one implementation, three statements of what the number means,
   * on the constant that decides when a room stops.
   */
  waitSlack: number;
  /** Persistent rooms are never garbage-collected. Defaults to TRUE here — see §7.6. */
  persistent: boolean;
  /** A join password is set. The password itself never leaves the server. */
  locked: boolean;
  listing: RoomListing;
  /** In `host` mode: who holds the remote. Null in `everyone` mode. */
  remoteHolderKey: string | null;
  members: Member[];
  playback: Playback;
  /**
   * Server epoch ms at the moment this snapshot was serialized.
   *
   * Stamped INSIDE the snapshot so every full room update doubles as a clock sample — that is what
   * lets a client that just joined start correcting immediately instead of waiting for its first
   * ping/pong round trip.
   */
  serverTime: number;
  createdAt: number;
  lastActivityAt: number;
}

export interface ChatMessage {
  id: string;
  /** Null for system lines ("Dad joined"). */
  memberKey: string | null;
  name: string;
  color: string;
  text: string;
  at: number;
  system?: boolean;
}

// ─── intents ─────────────────────────────────────────────────────────────────

/**
 * A single control intent, echoed back inside `sync`.
 *
 * ⚠️ **LAW 19 lives in this union.** `advance` moves to the next page or chapter and preserves
 * everything — the rate, the follow states, the blocks and, crucially, whether the room is playing.
 * `loadBook` changes the book and is destructive by design. movies has one `setSource` doing both.
 */
export type ControlAction =
  | { kind: "play" | "pause" | "seek"; at: BookPos }
  | { kind: "rate"; rate: number }
  /** Next page / next chapter. NON-destructive. */
  | { kind: "advance"; at: BookPos }
  /** A different book. Destructive: resets the rate, the clock, the blocks and the pinned timeline. */
  | { kind: "loadBook"; bookId: string }
  | { kind: "voice"; voice: string };

export interface RoomSettings {
  controlMode?: ControlMode;
  mode?: PlaybackMode;
  waitForEveryone?: boolean;
  waitSlack?: number;
  name?: string;
  persistent?: boolean;
  /** Set a password (non-empty), or clear the lock (`null` / `""`). Omit = unchanged. */
  password?: string | null;
  listing?: RoomListing;
}

// ─── client → server ─────────────────────────────────────────────────────────

export type ClientMsg =
  /**
   * `deviceId` distinguishes this browser's socket from the same person's other one. It is NOT an
   * identity — the cookie already answered that. `name` is honoured for guests only.
   */
  | { t: "hello"; deviceId: string; name?: string }
  | {
      t: "create";
      bookId: string;
      name: string;
      code?: string;
      mode: PlaybackMode;
      controlMode: ControlMode;
      waitForEveryone: boolean;
      waitSlack?: number;
      password?: string;
      listing?: RoomListing;
      persistent?: boolean;
      voiceId?: string;
    }
  | { t: "join"; code: string; password?: string }
  | { t: "leave" }
  /**
   * **Close the room for everybody. Host only.** `leave` gives up one seat; this ends the room.
   *
   * ⚠️ It did not exist until an audit walked into the consequence. `RoomGone`'s copy already told
   * people *"The room ended. Whoever started it closed it."* — prose describing a feature with no
   * code behind it, which is the same shape as §6.3's *"a takedown ends every room"* before P4 built
   * it. And §6.5's room quota (guest 2, user 20) counts rooms that have not ended, while persistent
   * rooms — the default — are **never** garbage-collected: without a way to end one, the quota is a
   * one-way trap. Reached on the twentieth Tuesday, it can never be escaped.
   */
  | { t: "end" }
  | { t: "control"; action: ControlAction }
  /**
   * Presence. Throttled to `AT_THROTTLE_MS`, plus one on every page turn.
   *
   * `visibleEndSid` is the last sentence on this device's displayed page/spread. It lets read
   * rooms distinguish "the room sentence is lower on the page I am already looking at" from
   * "the room has moved beyond my page". It is optional because an opening/reconnecting reader
   * may report before layout has produced a trustworthy visible range.
   */
  | { t: "at"; pos: BookPos; following: boolean; visibleEndSid?: string }
  /** `reason: null` clears this member's block. */
  | { t: "notready"; reason: NotReady | null; pageId?: string; etaSec?: number }
  | { t: "follow"; on: boolean }
  | { t: "chat"; text: string }
  | { t: "reaction"; emoji: string; sid?: string }
  | ({ t: "settings" } & RoomSettings)
  | { t: "passRemote" | "addHost" | "removeHost" | "kick"; key: string }
  | { t: "ping"; clientTime: number };

// ─── server → client ─────────────────────────────────────────────────────────

export type ServerMsg =
  | { t: "welcome"; actorKey: string; kind: "guest" | "user" | "god"; serverTime: number }
  | { t: "joined"; code: string }
  | { t: "room"; room: RoomSnapshot }
  /** The hot path. Carries who caused it, for the toast. */
  | { t: "sync"; playback: Playback; byKey: string | null; serverTime: number }
  | { t: "at"; key: string; pos: BookPos; following: boolean }
  /**
   * §7.6. Sent on enter when the room is not playing and has been idle > `RESUME_IDLE_MS`. The
   * client renders ONE button — *"Resume — Chapter 7, page 112, where you stopped on Tuesday."*
   *
   * **Nothing auto-plays.** It cannot, and it shouldn't.
   *
   * `personal` is this member's own `positions` row, when it exists and is expressed against the same
   * edition (LAW 2 — a position from a superseded edition is not offered, it is omitted). The room
   * bookmark and the personal bookmark are DIFFERENT ROWS and the app asks once: *"Resume together
   * (ch. 12, 3 days ago) or where you left off (ch. 14)?"*. Merging them automatically silently
   * destroys somebody's progress, which is why the server sends both and decides neither.
   */
  | {
      t: "resume";
      at: BookPos;
      since: number;
      personal: BookPos | null;
      personalUpdatedAt: number | null;
    }
  /** The pinned axis moved at a page boundary. `at` is the room's position re-expressed on it. */
  | { t: "timeline"; version: string; at: BookPos }
  | { t: "chat"; msg: ChatMessage }
  | { t: "chatHistory"; msgs: ChatMessage[] }
  | { t: "reaction"; emoji: string; byKey: string; name: string; sid?: string }
  /** A short-lived notice for ONE member — how a dropped block tells them the honest state. */
  | { t: "flash"; text: string; kind?: "good" | "bad" | "info" }
  | { t: "pong"; clientTime: number; serverTime: number }
  /**
   * ⚠️ **`kicked` is a separate reason from `host-ended`, and it is not a nicety.** They are two
   * different next actions: the room is gone versus the room is still running without you. Sending
   * `host-ended` for a kick told the removed member the room had closed — while everybody else kept
   * reading — and left `RoomGone`'s `kicked` copy permanently unreachable.
   */
  | { t: "ended"; reason?: "access-revoked" | "taken-down" | "host-ended" | "kicked" | "deleted" }
  | { t: "error"; message: string };

// ─── the permission rules, once ──────────────────────────────────────────────
//
// LAW 13's shape, one level down: the client calls these to enable and grey out controls, and the
// server calls the SAME functions to enforce. The client's answer is a courtesy; the server's is the
// authority. Two implementations of one rule diverge on the case nobody screenshotted.

export const isHost = (room: RoomSnapshot, key: string): boolean => room.hostKeys.includes(key);

export const isMember = (room: RoomSnapshot, key: string): boolean =>
  room.members.some((m) => m.key === key);

/**
 * May this member drive playback right now?
 *
 * ⚠️ **A guest can hold the remote.** In `controlMode: "everyone"` on a public book, a guest who
 * arrived from a shared link has full control, and that is what *"anon gets all features"* means
 * (§0, decision 1). The narrowing Karti accepted is regeneration, and only regeneration.
 */
export function canControl(room: RoomSnapshot, key: string): boolean {
  if (!isMember(room, key)) return false;
  if (room.controlMode === "everyone") return true;
  return isHost(room, key) || room.remoteHolderKey === key;
}

/** Settings, pass remote, add/remove hosts, kick. */
export const canAdmin = (room: RoomSnapshot, key: string): boolean => isHost(room, key);

/**
 * ⚠️ **A guest cannot be promoted to host unless the room's owner is also a guest.**
 *
 * Hosting is administrative — it can rename the room, change the password, kick people and end it —
 * and a `gst_` identity is a cookie with no recovery path and no account behind it. A guest room is
 * a different thing: it was made by a guest, so there is no account to protect and refusing here
 * would leave that room permanently unadministrable the moment its maker's cookie expired.
 */
export function canPromoteToHost(room: RoomSnapshot, candidate: Member): boolean {
  if (candidate.kind !== "guest") return true;
  const owner = room.members.find((m) => m.key === room.ownerKey);
  return owner?.kind === "guest" || room.ownerKey.startsWith("gst_");
}

// ─── names ───────────────────────────────────────────────────────────────────

export const MAX_MEMBER_NAME = 24;
export const MAX_ROOM_NAME = 60;
export const MAX_CHAT_TEXT = 500;

/**
 * Fold a name to the form two names are compared in.
 *
 * Case, spacing, punctuation and combining marks are all removed, because the attack this exists to
 * stop is not "a guest picks the same string" — it is a guest picking `K a r t i`, `KARTI.`,
 * `k_a_r_t_i` or `kártí` and being read as Karti at a glance in a presence rail.
 */
export function normalizeForCompare(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Clean a guest's claimed name, or refuse it.
 *
 * Refusal is `null`, and the caller keeps the previous name rather than substituting something —
 * silently rewriting somebody's name is worse than telling them it was not accepted. Control
 * characters and the bidi overrides go first: `‮` alone turns any display name into any other.
 */
export function sanitizeGuestName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const cleaned = raw
    // eslint-disable-next-line no-control-regex -- the point is to strip exactly these.
    .replace(/[\u0000-\u001f\u007f\u200b-\u200f\u2028-\u202e\ufeff]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_MEMBER_NAME);
  if (cleaned.length === 0) return null;
  if (normalizeForCompare(cleaned).length === 0) return null;
  return cleaned;
}

/**
 * Colours for the presence rail. Warm, and legible on all four reader themes — this rail sits beside
 * body text at a measured 66-character line, not on a black theater backdrop.
 */
export const MEMBER_COLORS = [
  "#c2703d",
  "#5b7f9c",
  "#8a6ba8",
  "#6f8f5e",
  "#b5555f",
  "#9c8036",
  "#4f8f86",
  "#a05f88",
  "#7a7fbd",
  "#8d6a4f",
] as const;

// ─── tuning, in one place ────────────────────────────────────────────────────
//
// §2.4's rule applied to the sync loop: one constant, two consumers. The server enforces the
// throttles and deadlines; the client drives its loop from the same numbers. Two copies of "how far
// apart is too far apart" is how a client that thinks it is in sync gets re-seeked forever.

/** The client's correction loop. movies' 250 ms, unchanged. */
export const TICK_MS = 250;

/**
 * Drift thresholds, in seconds. Tighter than movies (1.0 / 0.35) because a *highlight* on the wrong
 * sentence is instantly visible where a 0.9 s video offset is not — and not tighter than this,
 * because a re-seek on Safari restarts the decoder and clicks.
 */
export const DRIFT_PLAYING = 0.9;
export const DRIFT_PAUSED = 0.3;

/** `{t:"at"}` is throttled to 2/s per member. The server drops the excess rather than trusting it. */
export const AT_MIN_GAP_MS = 500;

/** Ten seconds of a room's life is one 30-byte frame, and it keeps the clock offset honest. */
export const PING_INTERVAL_MS = 10_000;

/** Reset on `welcome`. The first step is short because the common cause is a phone unlocking. */
export const RECONNECT_BACKOFF_MS: readonly number[] = [800, 1_500, 3_000, 6_000, 10_000];

/**
 * Echo suppression windows, monotonic-max. A programmatic op fires `play`/`pause`/`seeked` events
 * that must not come back as fresh intents; a *user* driving the native player IS a fresh intent.
 * The load window is longer because a page swap costs 30–200 ms and a seek during one must EXTEND
 * the window, never shorten it.
 */
export const SUPPRESS_SEEK_MS = 1500;
export const SUPPRESS_LOAD_MS = 1800;

/** Read mode's discrete correction: a 400 ms eased turn, never a continuous nudge. */
export const READ_TURN_MS = 400;

/**
 * ⚠️ **`bestRtt` decays 10% per 60 s, capped at 2000 ms.**
 *
 * movies names this as a wart and lives with it: `bestRtt` is monotonically decreasing and never
 * recovers. That was fine for a movie night on one wifi network. Here, once a 12 ms sample from the
 * sofa is on record, every subsequent 90 ms cellular sample is rejected except through the `< 250 ms`
 * escape — and on a link where every sample is over 250 ms the offset silently freezes at whatever it
 * was when the wifi dropped, for the rest of the session. Nothing errors; the read-along is just
 * quietly a quarter-second off for an hour.
 *
 * The cap is what keeps the relaxation from swallowing a garbage sample: past 2000 ms the estimate
 * would be worse than no estimate, so the bound stops there and the escape hatch does the rest.
 */
export const BEST_RTT_DECAY_PERIOD_MS = 60_000;
export const BEST_RTT_DECAY_RATE = 0.1;
export const BEST_RTT_CAP_MS = 2_000;
/** movies' "refresh escape": a sample this good re-anchors the offset even if it is not the best. */
export const RTT_REFRESH_MS = 250;

/** Idle longer than this and `enter()` offers `{t:"resume"}` instead of just dropping you in. */
export const RESUME_IDLE_MS = 30 * 60_000;

/**
 * ⚠️ **The disconnect grace exists for exactly ONE purpose: suppressing the "X left" notice on a
 * refresh.** It does NOT remove membership. A phone that locks, a laptop that sleeps and a household
 * that goes to bed must not lose their seats — §7.6.
 */
export const GRACE_MS = 3500;

// `ROOM_PREFETCH_PAGES` deliberately does NOT live here. How far ahead the server reads is a
// generation policy, not a term of the wire — it is `server/src/rooms/prefetch.ts`'s constant, and a
// second copy on the wire is §2.4's failure (four documents, four values for one physical constant).
